import { Layout } from "antd";
import { Outlet, useLocation } from "react-router-dom";
import { useMemo } from "react";

import { Chat } from "@app/features/chat/Chat";
import { useCabinet } from "@app/shared/lib/cabinet/CabinetProvider";
import { useDevice } from "@app/shared/lib/DeviceProvider";
import { useSchool } from "@app/shared/lib/hooks";
import { useUser } from "@app/shared/lib/hooks/useUser";
import { CabinetMenuWidget, parseCabinetMenuProps } from "@app/widgets/CabinetMenuWidget";
import type { CabinetMenuWidgetProps } from "@app/widgets/CabinetMenuWidget";

import { CookieNotification } from "../CookieNotification";
import { LoadingOverlay } from "../LoadingOverlay";
import { UIAppState } from "../UIAppState/UIAppState";

import { Header } from "./components/Header/Header";

import styles from "./BasicLayout.module.scss";

/** Parse cabinet menu settings from HTML string */
const parseMenuFromHtml = (html: string | null | undefined): CabinetMenuWidgetProps | null => {
  if (!html || !html.includes("data-ez-cabinet-menu")) return null;
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  const el = tmp.querySelector<HTMLElement>("[data-ez-cabinet-menu]");
  if (!el) return null;
  return parseCabinetMenuProps(el);
};

export const BasicLayout: React.FC = () => {
  const { deviceSize } = useDevice();
  const user = useUser();
  const school = useSchool();
  const { pages, rootPages, getPageBySegment, getPageBySegments, isLoading: cabinetLoading } = useCabinet();
  const location = useLocation();

  // Determine current page from URL
  const currentPage = useMemo(() => {
    if (!school.isCabinetEnabled || pages.length === 0) return null;
    const segments = location.pathname.split("/").filter(Boolean);
    if (segments.length === 0) return rootPages[0] ?? null;
    if (segments.length === 1) return getPageBySegment(segments[0]) ?? rootPages[0] ?? null;
    return getPageBySegments(...segments) ?? null;
  }, [school.isCabinetEnabled, pages, location.pathname, rootPages, getPageBySegment, getPageBySegments]);

  // Parse menu from current page's header or footer
  const cabinetMenuProps = useMemo<CabinetMenuWidgetProps | null>(() => {
    if (!currentPage) return null;
    return parseMenuFromHtml(currentPage.header?.designHTMLPublished)
      || parseMenuFromHtml(currentPage.footer?.designHTMLPublished);
  }, [currentPage]);

  if (user.user == null) return <LoadingOverlay position="absolute" />;

  if (school.isCabinetEnabled && cabinetLoading) {
    return <LoadingOverlay position="absolute" />;
  }

  const isVerticalMenu = cabinetMenuProps?.orientation === "vertical";

  return (
    <div data-size={deviceSize}>
      <Layout className={styles.content_layout} style={isVerticalMenu ? { flexDirection: "row" } : undefined}>
        {cabinetMenuProps
          ? <CabinetMenuWidget {...cabinetMenuProps} />
          : <Header />}
        <Layout.Content className="overflow-auto" style={isVerticalMenu ? { flex: 1 } : undefined}>
          <Outlet />
          <CookieNotification />
        </Layout.Content>
      </Layout>
      {school.supportChatEnabled && <Chat />}
      <UIAppState />
    </div>
  );
};
